import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trash2, NotebookPen } from "lucide-react";
import { api } from "@/lib/api";

const MOODS = [
  "😊", "🥰", "😤", "😭", "😩", "🤩", "😴", "🥺", "😳", "😌", "🤪", "🤤", "😒",
];

const paper = ["#FFF5F5", "#F4F1EA", "#FBEEE6", "#EEF3EC", "#FFF8E8"];

export default function Journal({ entries, setEntries, names }) {
  const [author, setAuthor] = useState(names[0]);
  const [mood, setMood] = useState("😊");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/journal", { author, mood, content });
      setEntries((e) => [data, ...e]);
      setContent("");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/journal/${id}`);
    setEntries((e) => e.filter((x) => x.id !== id));
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <section id="journal" className="max-w-6xl mx-auto px-6 py-16">
      <div className="mb-8">
        <p className="font-hand text-2xl text-[#D95F59]">a safe place for our hearts</p>
        <h2 className="font-display text-4xl tracking-tight flex items-center gap-3">
          <NotebookPen className="w-8 h-8 text-[#D95F59]" /> Our Journal
        </h2>
        <p className="text-[#756D6A] mt-1">Rant, vent, dream, confess — no judgement, just us.</p>
      </div>

      {/* composer */}
      <div className="card-paper rounded-3xl p-6 mb-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {names.map((n) => (
            <button
              key={n}
              data-testid={`journal-author-${n}`}
              onClick={() => setAuthor(n)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                author === n ? "bg-[#D95F59] text-white" : "bg-[#FFF5F5] text-[#756D6A]"
              }`}
            >
              {n}
            </button>
          ))}
          <span className="text-sm text-[#756D6A] ml-1">is feeling</span>
          <div className="flex flex-wrap gap-1">
            {MOODS.map((m) => (
              <button
                key={m}
                data-testid={`journal-mood-${m}`}
                onClick={() => setMood(m)}
                className={`text-xl w-9 h-9 rounded-full transition-all ${
                  mood === m ? "bg-[#FFF5F5] ring-2 ring-[#D95F59] scale-110" : "hover:bg-[#FFF5F5]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <textarea
          data-testid="journal-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          placeholder={`What's on your mind, ${author}?`}
          className="w-full rounded-2xl border border-[#EADEDA] px-4 py-3 bg-[#FFF5F5] outline-none focus:border-[#D95F59] resize-none text-lg"
        />
        <div className="flex justify-end mt-3">
          <button
            data-testid="save-journal-btn"
            onClick={save}
            disabled={!content.trim() || saving}
            className="rounded-full bg-[#D95F59] text-white px-6 py-2.5 hover:bg-[#C75D53] hover:scale-105 transition-all disabled:opacity-50"
          >
            {saving ? "Saving…" : "Pour it out 🤍"}
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="font-hand text-2xl text-[#756D6A] text-center py-8">
          No entries yet — be the first to share what's in your heart 💭
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {entries.map((en, i) => (
            <motion.div
              key={en.id}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 }}
              data-testid="journal-entry"
              style={{ background: paper[i % paper.length] }}
              className="rounded-2xl border border-[#EADEDA] p-5 shadow-soft hover:-translate-y-1 transition-transform group relative"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{en.mood || "💬"}</span>
                <span className="font-display text-lg">{en.author}</span>
                <span className="text-xs text-[#756D6A] ml-auto mr-5">{fmt(en.created_at)}</span>
              </div>
              <p className="text-[#2A2726] whitespace-pre-wrap leading-relaxed">{en.content}</p>
              <button
                data-testid="delete-journal-btn"
                onClick={() => remove(en.id)}
                className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4 text-[#D95F59]" />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
